import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import ChatPanel from './ChatPanel';

/**
 * Le bouton « Analyse IA » et son panneau, pour n'importe quel dashboard.
 *
 * ## Pourquoi un composant
 *
 * Le bouton, l'état du panneau, le repli de la barre latérale, la mesure de l'en-tête et le
 * rendu de `ChatPanel` ont été écrits trois fois — Google Ads, Microsoft Ads, Meta Ads — et il y
 * a sept dashboards de plus à servir. Dix copies d'une même mécanique, c'est dix endroits où
 * corriger le prochain défaut, et neuf chances de l'oublier.
 *
 * Chaque dashboard n'apporte donc plus que ce qui lui est propre : **ce qu'il envoie à
 * l'analyse**.
 *
 * ## Le panneau peut vivre à côté du bouton
 *
 * `ChatPanel` se rend par un portail dans `body` : sa position dans l'arbre React n'a aucune
 * incidence sur son placement à l'écran. On peut donc le poser dans la barre de filtres, auprès
 * du bouton qui l'ouvre, plutôt qu'à l'autre bout du composant.
 */
const AnalyseIA = ({
  token,
  compte,
  /** Les données de l'écran sont-elles là ? Un panneau ouvert sur un dashboard vide n'a rien à lire. */
  pret = true,
  /** L'abonnement ouvre-t-il l'analyse ? */
  autorise = true,
  /**
   * La charge à envoyer, **sous forme de fonction**.
   *
   * Elle est appelée au moment où la question part, et non à l'ouverture du panneau : les
   * composants d'un dashboard arrivent en plusieurs temps, et une charge figée trop tôt capture
   * un écran à moitié rempli.
   */
  construireCharge,
  /**
   * Le bouton réduit à son icône.
   *
   * Les dashboards consolidés rangent leurs actions dans une barre d'icônes — actualiser,
   * catégoriser, back-office — où un bouton libellé jurerait et pousserait les sélecteurs de
   * période hors de l'écran. Le `title` et l'`aria-label` nomment l'action, comme pour les
   * voisins : une icône seule ne dit rien à un lecteur d'écran.
   */
  iconeSeule = false
}) => {
  const [ouvert, setOuvert] = useState(false);

  /*
   * La barre latérale se replie pour rendre au panneau la largeur qu'il prend.
   *
   * `App` expose son setter sur `window` : ces composants sont voisins dans le même fichier mais
   * séparés par des milliers de lignes, et les relier par un contexte dédié demanderait de
   * traverser toute la hiérarchie pour un seul booléen.
   */
  useEffect(() => {
    const signaler = window.setAnalyseOuverte;
    if (!signaler) return undefined;
    signaler(ouvert);
    return () => signaler(false);
  }, [ouvert]);

  /*
   * Rendre au dashboard la largeur que le panneau prend, et placer celui-ci sous l'en-tête.
   *
   * Le panneau est en `position: fixed`, donc hors du flux : il recouvrirait le tableau sans que
   * rien ne s'en aperçoive. Le CSS rétrécit la zone de contenu tant que la classe est posée.
   *
   * Les deux hauteurs sont **mesurées** : un bandeau de connecteurs expirés ajoute 48 px, et la
   * navigation se replie sur deux lignes sous 1 024 px. Un nombre écrit en dur serait faux une
   * fois sur trois.
   */
  useEffect(() => {
    document.body.classList.toggle('panneau-analyse-ouvert', ouvert);

    const mesurer = () => {
      const contenu = document.querySelector('.app-layout-with-sidebar');
      const entete = document.querySelector('.app-header');
      const haut = contenu
        ? contenu.getBoundingClientRect().top
        : (entete ? entete.getBoundingClientRect().bottom : 0);
      document.documentElement.style.setProperty('--haut-panneau-analyse', `${Math.max(0, Math.round(haut))}px`);
      if (entete) {
        document.documentElement.style.setProperty(
          '--hauteur-entete-app',
          `${Math.round(entete.getBoundingClientRect().height)}px`
        );
      }
    };

    const nettoyer = () => {
      document.body.classList.remove('panneau-analyse-ouvert');
      document.documentElement.style.removeProperty('--haut-panneau-analyse');
      document.documentElement.style.removeProperty('--hauteur-entete-app');
    };

    if (!ouvert) {
      document.documentElement.style.removeProperty('--haut-panneau-analyse');
      document.documentElement.style.removeProperty('--hauteur-entete-app');
      return nettoyer;
    }

    mesurer();
    window.addEventListener('resize', mesurer);

    return () => {
      nettoyer();
      window.removeEventListener('resize', mesurer);
    };
  }, [ouvert]);

  return (
    <>
      {/*
        Le bouton s'efface quand le panneau est ouvert : il n'a plus rien à ouvrir, et le panneau
        porte déjà sa croix de fermeture. Le laisser ferait un second bouton sans effet à côté de
        ce qu'il était censé provoquer.
      */}
      {pret && autorise && !ouvert && (iconeSeule ? (
        <button
          onClick={() => setOuvert(true)}
          title="Analyse IA du dashboard affiché"
          aria-label="Analyse IA du dashboard affiché"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '8px', borderRadius: '8px', border: '1px solid #e5e7eb',
            backgroundColor: 'white', cursor: 'pointer', fontSize: '0.875rem'
          }}
        >
          <Sparkles size={15} color="#7c2d12" />
        </button>
      ) : (
        <button
          onClick={() => setOuvert(true)}
          /*
           * La classe des onglets de l'en-tête, plutôt que leurs couleurs recopiées : le bouton
           * suivra d'office une évolution de la charte. Seule la taille est précisée en ligne,
           * `.nav-btn` étant dimensionné pour une barre de navigation.
           */
          className="nav-btn"
          style={{
            padding: '8px 16px',
            gap: '10px',
            fontSize: '0.9em',
            fontWeight: '500',
            whiteSpace: 'nowrap'
          }}
          title="Analyse IA du dashboard affiché"
        >
          <Sparkles size={16} color="#7c2d12" />
          Analyse IA
        </button>
      ))}

      <ChatPanel
        isOpen={ouvert}
        onClose={() => setOuvert(false)}
        token={token}
        accountId={compte?.sourceId || compte?.id || ''}
        accountName={compte?.name || ''}
        analysisResult={null}
        dashboardStream={construireCharge}
        sansOverlay
      />
    </>
  );
};

export default AnalyseIA;
